import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IBreadcrumbItem } from 'src/app/models';
import { CommunicationUserType, Theme, UserSortType } from 'src/app/models/enums';
import { SuggestionSortType } from 'src/app/models/enums/suggestionSortType';
import { UserRole } from 'src/app/models/enums/userRole';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private keys = {
    language: `language`,
    routeName: `route`,
    theme: `theme`,
    role: `role`,
    userSortType: `userSortType`,
    userInfo: `userInfo`,
    area: `area`,
    servicesAreas: `servicesAreas`,
    hiddenRoutes: `hiddenRoutes`,
    userSelected: `userSelected`,
    driversColors: 'driversColors',
    vehiclesColors: 'vehiclesColors',
    bookingsFilters: `bookingsFilters`,
    usersFilters: `usersFilters`,
    tripsFilters: `tripsFilters`,
    suggestionsSortType: `suggestionsSortType`,
    suggestionsFilters: `suggestionsFilters`,
    messagesFilters: `messagesFilters`,
    bookingsPageSize: `bookingsPageSize`,
    availabilitiesPageSize: `availabilitiesPageSize`,
    usersPageSize: `usersPageSize`,
    restrictionsPageSize: `restrictionsPageSize`,
    usersLastListType: `usersLastListType`,
    suggestionsLastListType: `suggestionsLastListType`,
    clients: `clients`
  };

  private onRouteNameChange = new BehaviorSubject(null);
  onRouteNameChange$ = this.onRouteNameChange.asObservable();

  private breadcrumbSubject = new BehaviorSubject<IBreadcrumbItem[] | null>(null);
  breadcrumb$ = this.breadcrumbSubject.asObservable();

  public setBreadcrumb(items: IBreadcrumbItem[]): void {
    this.breadcrumbSubject.next(items);
  }

  public setBreadcrumbLoading(): void {
    this.breadcrumbSubject.next([]);
  }

  public clearBreadcrumb(): void {
    this.breadcrumbSubject.next(null);
  }

  private titleKeySubject = new BehaviorSubject<string | null>(null);
  titleKey$ = this.titleKeySubject.asObservable();

  public setTitleKey(key: string | null): void {
    this.titleKeySubject.next(key);
  }

  public async setLanguage(language: string): Promise<void> {
    return this.setByKey(this.keys.language, language);
  }

  public async getLanguage(): Promise<string> {
    return this.getByKey(this.keys.language);
  }

  public async removeLanguage(): Promise<void> {
    return this.removeByKey(this.keys.language);
  }

  public async setRouteName(routeName: string): Promise<void> {
    await this.setByKey(this.keys.routeName, routeName);
    this.onRouteNameChange.next(null);
  }

  public async getRouteName(): Promise<string | null> {
    return this.getByKey(this.keys.routeName);
  }

  public async removeRouteName(): Promise<void> {
    return this.removeByKey(this.keys.routeName);
  }

  public async setTheme(theme: Theme): Promise<void> {
    return this.setByKey(this.keys.theme, theme);
  }

  public async getTheme(): Promise<Theme> {
    return this.getByKey(this.keys.theme);
  }

  public async removeTheme(): Promise<void> {
    return this.removeByKey(this.keys.theme);
  }

  public async setRole(role: UserRole): Promise<void> {
    return this.setByKey(this.keys.role, role);
  }

  public async getRole(): Promise<UserRole> {
    return this.getByKey(this.keys.role);
  }

  public async removeRole(): Promise<void> {
    return this.removeByKey(this.keys.role);
  }

  public async setUserSortType(userSortType: UserSortType): Promise<void> {
    return this.setByKey(this.keys.userSortType, userSortType);
  }

  public async getUserSortType(): Promise<UserSortType> {
    return this.getByKey(this.keys.userSortType);
  }

  public async removeUserSortType(): Promise<void> {
    return this.removeByKey(this.keys.userSortType);
  }
  
  public async setSuggestionsSortType(suggestionSortType: SuggestionSortType): Promise<void> {
    return this.setByKey(this.keys.suggestionsSortType, suggestionSortType);
  }

  public async getSuggestionsSortType(): Promise<SuggestionSortType> {
    return this.getByKey(this.keys.suggestionsSortType);
  }

  public async removeSuggestionsSortType(): Promise<void> {
    return this.removeByKey(this.keys.suggestionsSortType);
  }

  public async setUserInfo(userInfo: any): Promise<void> {
    return this.setByKey(this.keys.userInfo, userInfo);
  }

  public async getUserInfo(): Promise<any> {
    return this.getByKey(this.keys.userInfo);
  }

  public async removeUserInfo(): Promise<void> {
    return this.removeByKey(this.keys.userInfo);
  }

  public async getUserSelected(): Promise<unknown> {
    return this.getByKey(this.keys.userSelected);
  }

  public async setUserSelected(userSelected: any): Promise<void> {
    return this.setByKey(this.keys.userSelected, userSelected);
  }

  public async removeUserSelected(): Promise<void> {
    return this.removeByKey(this.keys.userSelected);
  }

  public async setArea(area: any): Promise<void> {
    return this.setByKey(this.keys.area, area);
  }

  public async getArea(): Promise<string> {
    return this.getByKey(this.keys.area);
  }

  public async removeArea(): Promise<void> {
    return this.removeByKey(this.keys.area);
  }

  public async setServicesAreas(area: any): Promise<void> {
    return this.setByKey(this.keys.servicesAreas, area);
  }

  public async getServicesAreas(): Promise<string> {
    return this.getByKey(this.keys.servicesAreas);
  }

  public async removeServicesAreas(): Promise<void> {
    return this.removeByKey(this.keys.servicesAreas);
  }

  public async setHiddenRoutes(hiddenRoute: any): Promise<void> {
    return this.setByKey(this.keys.hiddenRoutes, hiddenRoute);
  }

  public async getHiddenRoutes(): Promise<string> {
    return this.getByKey(this.keys.hiddenRoutes);
  }

  public async removeHiddenRoute(): Promise<void> {
    return this.removeByKey(this.keys.hiddenRoutes);
  }

  public async setDriversColors(driversColors: any): Promise<void> {
    return this.setByKey(this.keys.driversColors, driversColors);
  }

  public async getDriversColors(): Promise<any> {
    return this.getByKey(this.keys.driversColors);
  }

  public async removeDriversColors(): Promise<void> {
    return this.removeByKey(this.keys.driversColors);
  }

  public async setVehiclesColors(vehiclesColors: any): Promise<void> {
    return this.setByKey(this.keys.vehiclesColors, vehiclesColors);
  }

  public async getVehiclesColors(): Promise<any> {
    return this.getByKey(this.keys.vehiclesColors);
  }

  public async removeVehiclesColors(): Promise<void> {
    return this.removeByKey(this.keys.vehiclesColors);
  }

  public async getBookingsFilters(): Promise<string> {
    return this.getByKey(this.keys.bookingsFilters);
  }

  public async setBookingsFilters(filters: any): Promise<void> {
    return this.setByKey(this.keys.bookingsFilters, filters);
  }

  public async removeBookingsFilters(): Promise<void> {
    return this.removeByKey(this.keys.bookingsFilters);
  }

  public async getUsersFilters(): Promise<string> {
    return this.getByKey(this.keys.usersFilters);
  }

  public async setUsersFilters(filters: any): Promise<void> {
    return this.setByKey(this.keys.usersFilters, filters);
  }

  public async removeUsersFilters(): Promise<void> {
    return this.removeByKey(this.keys.usersFilters);
  }

  public async getTripsFilters(): Promise<string> {
    return this.getByKey(this.keys.tripsFilters);
  }

  public async setTripsFilters(filters: any): Promise<void> {
    return this.setByKey(this.keys.tripsFilters, filters);
  }

  public async removeTripsFilters(): Promise<void> {
    return this.removeByKey(this.keys.tripsFilters);
  }

  public async getSuggestionsFilters(): Promise<string> {
    return this.getByKey(this.keys.suggestionsFilters);
  }

  public async setSuggestionsFilters(filters: any): Promise<void> {
    return this.setByKey(this.keys.suggestionsFilters, filters);
  }

  public async removeSuggestionsFilters(): Promise<void> {
    return this.removeByKey(this.keys.suggestionsFilters);
  }

  public async getMessagesFilters(): Promise<string> {
    return this.getByKey(this.keys.messagesFilters);
  }

  public async setMessagesFilters(filters: any): Promise<void> {
    return this.setByKey(this.keys.messagesFilters, filters);
  }

  public async removeMessagesFilters(): Promise<void> {
    return this.removeByKey(this.keys.messagesFilters);
  }

  public async setBookingsPageSize(size: number): Promise<void> {
    return this.setByKey(this.keys.bookingsPageSize, String(size));
  }

  public async getBookingsPageSize(): Promise<number | null> {
    const value = await this.getByKey(this.keys.bookingsPageSize);
    return value ? Number(value) : null;
  }

  public async setAvailabilitiesPageSize(size: number): Promise<void> {
    return this.setByKey(this.keys.availabilitiesPageSize, String(size));
  }

  public async getAvailabilitiesPageSize(): Promise<number | null> {
    const value = await this.getByKey(this.keys.availabilitiesPageSize);
    return value ? Number(value) : null;
  }

  public async setUsersPageSize(size: number): Promise<void> {
    return this.setByKey(this.keys.usersPageSize, String(size));
  }

  public async getUsersPageSize(): Promise<number | null> {
    const value = await this.getByKey(this.keys.usersPageSize);
    return value ? Number(value) : null;
  }

  public async setRestrictionsPageSize(size: number): Promise<void> {
    return this.setByKey(this.keys.restrictionsPageSize, String(size));
  }

  public async getRestrictionsPageSize(): Promise<number | null> {
    const value = await this.getByKey(this.keys.restrictionsPageSize);
    return value ? Number(value) : null;
  }

  public async setUsersLastListType(listType: CommunicationUserType): Promise<void> {
    return this.setByKey(this.keys.usersLastListType, listType);
  }

  public async getUsersLastListType(): Promise<CommunicationUserType | null> {
    return this.getByKey(this.keys.usersLastListType);
  }

  public async setSuggestionsLastListType(listType: CommunicationUserType): Promise<void> {
    return this.setByKey(this.keys.suggestionsLastListType, listType);
  }

  public async getSuggestionsLastListType(): Promise<CommunicationUserType | null> {
    return this.getByKey(this.keys.suggestionsLastListType);
  }

  public async getClients(): Promise<string> {
    return this.getByKey(this.keys.clients);
  }

  public async setClients(clients: any): Promise<void> {
    return this.setByKey(this.keys.clients, clients);
  }

  public async removeClients(): Promise<void> {
    return this.removeByKey(this.keys.clients);
  }

  public async clearStorage(): Promise<void> {
    localStorage.clear();
  }

  // eslint-disable-next-line
  private async getByKey(key: string): Promise<any> {
    try {
      const value = await localStorage.getItem(key);
      return value;
    } catch (error) {
      console.error(error);
    }
  }

  private async setByKey(key: string, value: any): Promise<void> {
    try {
      const normalized = this.normalizeForStorage(value);
      if (normalized === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, normalized);
      }
    } catch (error) {
      console.error(error);
    }
  }

  private normalizeForStorage(value: any): string | null {
    if (value == null || value === '') return null;

    const str = typeof value === 'string' ? value : JSON.stringify(value);
    if (!str || str === 'null') return null;

    let parsed: any;
    try {
      parsed = JSON.parse(str);
    } catch {
      return str;
    }

    if (parsed === null) return null;

    if (Array.isArray(parsed)) {
      const clean = parsed.filter((item: any) => item != null);
      return clean.length ? JSON.stringify(clean) : null;
    }

    if (typeof parsed === 'object') {
      return Object.keys(parsed).length ? str : null;
    }

    return str;
  }

  // eslint-disable-next-line
  private async removeByKey(key: string): Promise<any> {
    try {
      await localStorage.removeItem(key);
    } catch (error) {
      console.error(error);
    }
  }
}
